import React, { useState, useMemo } from "react";
import type { TabStructure, SectionStructure, ColumnStructure } from "../utils/buildTabsFromMeta";
import { FieldRenderer } from "./FieldRenderer";
import { cn } from "@/lib/utils";
import { useDetailPageTabs } from "@/contexts/DetailPageTabsContext";
import { MetaFieldEditModal } from "./MetaFieldEditModal";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { useFieldDependencies } from "@/hooks/useFieldDependencies";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { DetailDensity, DetailTextSize } from "@/lib/store/preferencesStore";
import { tabHasVisibleFields } from "../utils/tabHasVisibleFields";
import { CARD, CARD_CLICKABLE } from "@/config/design-tokens";

// ── Density class maps ──────────────────────────────────────
// flex+gap instead of space-y: sections whose widget renders nothing
// (e.g. DisabledBanner on a non-archived item) are display:none via
// empty:hidden and consume NO gap — space-y gave them a phantom margin.
const SECTION_GAP: Record<DetailDensity, string> = {
  compact: "flex flex-col gap-3",
  default: "flex flex-col gap-6",
  spacious: "flex flex-col gap-8",
};
const CARD_PADDING: Record<DetailDensity, string> = {
  compact: "px-4 py-4",
  default: "px-6 py-8",
  spacious: "px-8 py-10",
};
const CARD_INNER_GAP: Record<DetailDensity, string> = {
  compact: "space-y-2",
  default: "space-y-4",
  spacious: "space-y-5",
};
const GRID_GAP: Record<DetailDensity, string> = {
  compact: "gap-2",
  default: "gap-4",
  spacious: "gap-5",
};
const COL_GAP: Record<DetailDensity, string> = {
  compact: "space-y-3",
  default: "space-y-6",
  spacious: "space-y-8",
};
const APPLE_ROW_PX: Record<DetailDensity, string> = {
  compact: "px-3",
  default: "px-4",
  spacious: "px-5",
};
const APPLE_ROW_PY: Record<DetailDensity, string> = {
  compact: "py-2",
  default: "py-5",
  spacious: "py-6",
};
const APPLE_ROW_FIRST: Record<DetailDensity, string> = {
  compact: "pt-2.5 pb-2",
  default: "pt-5 pb-4",
  spacious: "pt-6 pb-5",
};
const APPLE_DIVIDER_MX: Record<DetailDensity, string> = {
  compact: "mx-3",
  default: "mx-4",
  spacious: "mx-5",
};
const TEXT_LABEL: Record<DetailTextSize, string> = {
  small: "text-xs",
  medium: "text-sm",
  large: "text-[15px]",
};
import type { DocFieldSchema } from "@/lib/erp/meta";
import type { DocTypeSchema } from "@/lib/erp/meta";
import { ShopifyStyleRenderer } from "./viewStyles/ShopifyStyleRenderer";
import { formatFieldValue } from "./utils/shopifyFormatting";
import { CONNECTIONS_TAB_ID } from "@/features/common/doc-connections/useConnectionsTab";
import { QUICK_CONNECTIONS_TAB_ID } from "@/features/common/doc-connections/useQuickConnectionsTab";
import { DASHBOARD_TAB_ID } from "@/features/common/doc-dashboard/useDashboardTab";
import { ITEM_PRICE_TAB_ID } from "@/features/inventory/item-price/useItemPriceTab";
import { LOYALTY_TAB_ID } from "@/features/customers/customer-detail/useLoyaltyTab";
import { ADDRESSES_TAB_ID } from "@/features/customers/customer-detail/useAddressesTab";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";
import { SectionHeader } from "./SectionHeader";
import { FieldInfoTooltip } from "./FieldInfoTooltip";

/** Convert section to a nav ID for SectionNavRail scroll spy.
 *  Prefer section.name (= schema id like "product-details") over label for stability. */
function sectionNavId(section: { label?: string; name?: string }): string {
  return section.name || section.label?.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") || "";
}

interface TabRendererProps {
  tabs: TabStructure[];
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  disabled?: boolean;
  onSave?: () => void | Promise<void>; // Can be async
  onSubmit?: (patch: Record<string, any>) => Promise<void> | void;
  onSaveSuccess?: (updatedDoc: Record<string, any>) => void;
  isSaving?: boolean;
  resetKey?: string | number; // Reset key for child table resync
  layoutStyle?: 'apple' | 'grid' | 'shopify'; // Optional override for layout style
  viewMode?: 'system' | 'experience'; // Controls child table column filtering
  meta?: DocTypeSchema; // Meta schema for field resolution
  experienceViewStyle?: 'shopify' | 'apple' | 'system'; // Style from DocTypeView when in experience mode
  documentName?: string; // Document identifier (name/id) - required for attachment fields
  isNew?: boolean; // Whether this is a new document (create mode)
  /** Field-level error map from server insert failure (Plan 166 Epic 02) */
  fieldErrors?: Record<string, string>;
  /** Clear a single field error when user starts editing */
  onClearFieldError?: (fieldname: string) => void;
}

/**
 * Render sections within a tab - Apple style container
 */
function AppleStyleSectionRenderer({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  onSubmit,
  onSaveSuccess,
  isSaving,
  viewMode: _viewMode, // Accepted for consistency but not used (MetaFieldEditModal doesn't need it)
  metaFields: _metaFields, // Accepted for type consistency but not used
  documentName,
  density = 'default',
  textSize = 'medium',
}: {
  section: SectionStructure;
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  disabled?: boolean;
  onSave?: () => void;
  onSubmit?: (patch: Record<string, any>) => Promise<void> | void;
  onSaveSuccess?: (updatedDoc: Record<string, any>) => void;
  isSaving?: boolean;
  viewMode?: 'system' | 'experience';
  metaFields?: DocFieldSchema[];
  documentName?: string;
  density?: DetailDensity;
  textSize?: DetailTextSize;
}) {
  // ALL HOOKS FIRST - no early returns before all hooks are called
  const [modalOpen, setModalOpen] = useState(false);
  const { language } = useLanguage();
  
  // Translate section label
  const translatedSectionLabel = useMemo(() => {
    return section.label ? getSectionTitleTranslation(section.label, language) : null;
  }, [section.label, language]);

  // Memoize allFields to ensure stable reference for useMemo
  const allFields = useMemo(
    () => section.columns.flatMap((column) => column.fields),
    [section.columns]
  );

  // Detect "More Info" / unmapped sections - bypass dependency filtering for these
  const isMoreInfoSection = useMemo(() => {
    return (
      section.name === 'more-info' ||
      section.name === 'more-info-auto' ||
      (section as any)._collectUnmapped === true
    );
  }, [section.name, section]);

  // Compute dependencies once for this section
  const { visibleFieldNames } = useFieldDependencies(
    allFields,
    formData
  );

  // For "More Info" sections, show all fields regardless of dependencies
  // This restores the old CustomFieldBucket behavior where unmapped fields
  // are always visible (advanced users can see everything)
  const allFieldNames = useMemo(
    () => new Set(allFields.map(f => f.fieldname)),
    [allFields]
  );

  // System View = show everything (raw mode, no dependency filtering)
  // Experience View = use dependency filtering (smart mode)
  // More Info sections = always show everything
  const effectiveVisibleFieldNames = useMemo(() => {
    if (_viewMode === 'system') {
      // System View: show all fields (except hidden ones, which are already filtered out)
      return allFieldNames;
    }
    if (isMoreInfoSection) {
      // More Info sections: show all fields
      return allFieldNames;
    }
    // Experience View: use dependency filtering
    return visibleFieldNames;
  }, [_viewMode, isMoreInfoSection, allFieldNames, visibleFieldNames]);

  // Debug logging for System View and More Info sections
  if (import.meta.env.DEV && (_viewMode === 'system' || isMoreInfoSection)) {
    console.group(`[TabRenderer] ${_viewMode === 'system' ? 'System View' : 'More Info'} section: ${section.name}`);
    console.log('Section:', {
      name: section.name,
      label: section.label,
      columnsCount: section.columns.length,
      allFieldsCount: allFields.length,
      isMoreInfoSection,
      viewMode: _viewMode,
    });
    console.log('Dependency filtering:', _viewMode === 'system' || isMoreInfoSection ? 'BYPASSED (showing all fields)' : 'ACTIVE');
    console.log('Total fields:', allFields.length);
    console.log('Visible fields (after filtering):', Array.from(effectiveVisibleFieldNames).length);
    if (allFields.length > 0) {
      console.log('First 5 fields:', allFields.slice(0, 5).map(f => f.fieldname));
    }
    console.groupEnd();
  }

  // NOW early returns are safe - after all hooks
  if (section.columns.length === 0) {
    return null;
  }

  if (allFields.length === 0) {
    return null;
  }

  // For More Info sections, don't early-return even if dependencies filter everything
  // (we're bypassing dependencies anyway, so this check is just for empty sections)
  const visibleFields = allFields.filter((field) => 
    effectiveVisibleFieldNames.has(field.fieldname)
  );

  if (!isMoreInfoSection && visibleFields.length === 0) {
    return null;
  }

  return (
    <>
      <div>
        {translatedSectionLabel && <SectionHeader label={translatedSectionLabel} textSize={textSize} />}
        <div
          className={CARD_CLICKABLE}
          onClick={() => setModalOpen(true)}
        >
          <div className="space-y-0">
            {visibleFields
              .map((field, _index, visibleFields) => {
                const isLast = _index === visibleFields.length - 1;
                const value = formData[field.fieldname];
                const displayValue = formatFieldValue(field, value);
                // @ts-expect-error - Variable 'doctype' may be from global scope
                const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);

                return (
                  <React.Fragment key={field.fieldname}>
                    <div className={cn(
                      "flex items-center justify-between",
                      APPLE_ROW_PX[density],
                      _index === 0
                        ? APPLE_ROW_FIRST[density]
                        : APPLE_ROW_PY[density]
                    )}>
                      <span className={cn(TEXT_LABEL[textSize], "text-muted-foreground")}>{resolvedLabel.label}</span>
                      <div className="flex items-center gap-2">
                        <span className={TEXT_LABEL[textSize]}>{displayValue}</span>
                        {field.description && <FieldInfoTooltip description={field.description} label={resolvedLabel.label} />}
                      </div>
                    </div>
                    {!isLast && <div className={APPLE_DIVIDER_MX[density]} />}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      </div>

      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        section={section}
        formData={formData}
        onFieldChange={onFieldChange}
        onSubmit={onSubmit}
        onSaveSuccess={onSaveSuccess}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
        documentName={documentName}
      />
    </>
  );
}

/**
 * Render fields within a column (for grid layout)
 */
function ColumnRenderer({
  column,
  formData,
  onFieldChange,
  disabled,
  resetKey,
  visibleFieldNames,
  fieldReadOnly,
  fieldRequired,
  viewMode,
  documentName,
  density = 'default',
}: {
  column: ColumnStructure;
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  disabled?: boolean;
  resetKey?: string | number;
  visibleFieldNames: Set<string>;
  fieldReadOnly: Record<string, boolean>;
  fieldRequired: Record<string, boolean>;
  viewMode?: 'system' | 'experience';
  documentName?: string;
  density?: DetailDensity;
}) {
  return (
    <div className={COL_GAP[density]}>
      {column.fields
        .filter((field) => visibleFieldNames.has(field.fieldname))
        .map((field) => (
          <FieldRenderer
            key={field.fieldname}
            field={field}
            value={formData[field.fieldname]}
            onChange={(value) => onFieldChange(field.fieldname, value)}
            disabled={disabled}
            isReadOnly={fieldReadOnly[field.fieldname]}
            isRequired={fieldRequired[field.fieldname]}
            resetKey={resetKey}
            viewMode={viewMode}
            documentName={documentName}
            parentDoc={formData}
          />
        ))}
    </div>
  );
}

/**
 * Render sections within a tab - Grid layout (old style)
 */
function GridStyleSectionRenderer({
  section,
  formData,
  onFieldChange,
  disabled,
  resetKey,
  viewMode,
  onSaveSuccess: _onSaveSuccess, // Accepted for type consistency but not used (GridStyle doesn't use modals)
  metaFields: _metaFields, // Accepted for type consistency but not used
  documentName,
  density = 'default',
  textSize = 'medium',
}: {
  section: SectionStructure;
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  disabled?: boolean;
  resetKey?: string | number;
  viewMode?: 'system' | 'experience';
  onSubmit?: (patch: Record<string, any>) => Promise<void> | void;
  onSaveSuccess?: (updatedDoc: Record<string, any>) => void;
  metaFields?: DocFieldSchema[];
  documentName?: string;
  density?: DetailDensity;
  textSize?: DetailTextSize;
}) {
  // ALL HOOKS FIRST - no early returns before all hooks are called
  const { language } = useLanguage();
  
  // Translate section label
  const translatedSectionLabel = useMemo(() => {
    return section.label ? getSectionTitleTranslation(section.label, language) : null;
  }, [section.label, language]);
  
  // Memoize allFields to ensure stable reference for useMemo
  const allFields = useMemo(
    () => section.columns.flatMap((column) => column.fields),
    [section.columns]
  );

  // Detect "More Info" / unmapped sections - bypass dependency filtering for these
  const isMoreInfoSection = useMemo(() => {
    return (
      section.name === 'more-info' ||
      section.name === 'more-info-auto' ||
      (section as any)._collectUnmapped === true
    );
  }, [section.name, section]);

  // Compute dependencies once for this section
  const { visibleFieldNames, fieldReadOnly, fieldRequired } = useFieldDependencies(
    allFields,
    formData
  );

  // For "More Info" sections, show all fields regardless of dependencies
  const allFieldNames = useMemo(
    () => new Set(allFields.map(f => f.fieldname)),
    [allFields]
  );

  // System View = show everything (raw mode, no dependency filtering)
  // Experience View = use dependency filtering (smart mode)
  // More Info sections = always show everything
  const effectiveVisibleFieldNames = useMemo(() => {
    if (viewMode === 'system') {
      // System View: show all fields (except hidden ones, which are already filtered out)
      return allFieldNames;
    }
    if (isMoreInfoSection) {
      // More Info sections: show all fields
      return allFieldNames;
    }
    // Experience View: use dependency filtering
    return visibleFieldNames;
  }, [viewMode, isMoreInfoSection, allFieldNames, visibleFieldNames]);

  // NOW early returns are safe - after all hooks
  if (section.columns.length === 0) {
    return null;
  }

  return (
    <div>
      {translatedSectionLabel && <SectionHeader label={translatedSectionLabel} textSize={textSize} />}
      <div className={cn(CARD, "text-card-foreground")}>
        <div className={CARD_PADDING[density]}>
          <div className={CARD_INNER_GAP[density]}>
            {/* Render columns - if multiple columns, use grid layout */}
            <div
              className={cn(
                "grid",
                GRID_GAP[density],
                section.columns.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"
              )}
            >
              {section.columns.map((column, colIndex) => (
                <ColumnRenderer
                  key={column.name || colIndex}
                  column={column}
                  formData={formData}
                  onFieldChange={onFieldChange}
                  disabled={disabled}
                  resetKey={resetKey}
                  visibleFieldNames={effectiveVisibleFieldNames}
                  fieldReadOnly={fieldReadOnly}
                  fieldRequired={fieldRequired}
                  viewMode={viewMode}
                  documentName={documentName}
                  density={density}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Render sections within a tab - chooses style based on preference or override
 * 
 * CRITICAL: Uses component variable pattern to ensure React correctly tracks
 * hook calls when switching between renderers. Never call renderers as functions.
 */
function SectionRenderer({
  section,
  formData,
  onFieldChange,
  disabled,
  onSave,
  onSubmit,
  onSaveSuccess,
  isSaving,
  resetKey,
  layoutStyle,
  viewMode,
  metaFields,
  experienceViewStyle,
  documentName,
  meta,
  isNew,
  density = 'default',
  textSize = 'medium',
  fieldErrors,
  onClearFieldError,
}: {
  section: SectionStructure;
  formData: Record<string, any>;
  onFieldChange: (fieldname: string, value: any) => void;
  disabled?: boolean;
  onSave?: () => void;
  onSubmit?: (patch: Record<string, any>) => Promise<void> | void;
  onSaveSuccess?: (updatedDoc: Record<string, any>) => void;
  isSaving?: boolean;
  resetKey?: string | number;
  layoutStyle?: 'apple' | 'grid' | 'shopify';
  viewMode?: 'system' | 'experience';
  metaFields?: DocFieldSchema[];
  experienceViewStyle?: 'shopify' | 'apple' | 'system';
  documentName?: string;
  meta?: DocTypeSchema;
  isNew?: boolean;
  density?: DetailDensity;
  textSize?: DetailTextSize;
  fieldErrors?: Record<string, string>;
  onClearFieldError?: (fieldname: string) => void;
}) {
  // ALL HOOKS FIRST - called unconditionally
  const globalStyle = usePreferencesStore((state) => state.fieldLayoutStyle);
  
  // Style detection: use view config style when in experience mode
  const effectiveStyle = 
    layoutStyle ?? // Manual override (highest priority)
    (viewMode === 'experience' && experienceViewStyle) ?? // From DocTypeView.style
    globalStyle ?? // User preference
    'grid'; // Default fallback

  // Choose component (not function) - must be after all hooks
  // Using component variable pattern to ensure React tracks correctly
  let RendererComponent;
  if (effectiveStyle === 'shopify') {
    RendererComponent = ShopifyStyleRenderer;
  } else if (effectiveStyle === 'apple') {
    RendererComponent = AppleStyleSectionRenderer;
  } else {
    RendererComponent = GridStyleSectionRenderer;
  }

  // Render as JSX component (never call as function)
  return (
    <RendererComponent
      section={section}
      formData={formData}
      onFieldChange={onFieldChange}
      disabled={disabled}
      onSave={onSave}
      onSubmit={onSubmit}
      onSaveSuccess={onSaveSuccess}
      isSaving={isSaving}
      resetKey={resetKey}
      viewMode={viewMode}
      metaFields={metaFields}
      {...({ experienceViewStyle, documentName, meta, isNew, density, textSize, fieldErrors, onClearFieldError } as any)}
    />
  );
}

/**
 * Tab renderer component
 * 
 * Renders tabs, sections, columns, and fields based on meta structure.
 * Completely meta-driven - no hardcoded layouts.
 * 
 * Note: Tab list UI is rendered in FloatingNav, not here.
 */
export function TabRenderer({ tabs, formData, onFieldChange, disabled, onSave, onSubmit, onSaveSuccess: _onSaveSuccess, isSaving, resetKey, layoutStyle, viewMode, meta, experienceViewStyle, documentName, isNew, fieldErrors, onClearFieldError }: TabRendererProps) {
  // ============================================================================
  // ALL HOOKS MUST BE CALLED UNCONDITIONALLY AT THE TOP - BEFORE ANY EARLY RETURNS
  // ============================================================================
  
  const { setTabs, tabs: contextTabs, activeTab, setActiveTab } = useDetailPageTabs();

  // Detail density: read from store with selectors, force default on mobile
  const detailDensity = usePreferencesStore((s) => s.detailDensity);
  const detailTextSize = usePreferencesStore((s) => s.detailTextSize);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const effectiveDensity = isDesktop ? detailDensity : 'default';

  // Collect ALL fields from all tabs to compute visibility once
  const allFieldsFromAllTabs = React.useMemo(() => {
    const fields: DocFieldSchema[] = [];
    for (const tab of tabs) {
      for (const section of tab.sections) {
        for (const column of section.columns) {
          fields.push(...column.fields);
        }
      }
    }
    return fields;
  }, [tabs]);

  // Compute field dependencies once for all fields
  // This is the single source of truth for field visibility
  const { visibleFieldNames } = useFieldDependencies(allFieldsFromAllTabs, formData);

  // Filter tabs to only include those with visible fields
  // System View = show all tabs (raw mode, no filtering)
  // Experience View = filter tabs based on field visibility
  const visibleTabs = React.useMemo(() => {
    
    // System View: show all tabs (no filtering)
    if (viewMode === 'system') {
      return tabs;
    }
    
    // Experience View: filter tabs based on field visibility
    // If we don't have visibility info yet (e.g., on initial mount), fall back to all tabs
    if (!visibleFieldNames || visibleFieldNames.size === 0) {
      return tabs;
    }
    
    const filtered = tabs.filter(tab => tabHasVisibleFields(tab, visibleFieldNames));
    return filtered;
  }, [tabs, visibleFieldNames, viewMode]);

  // Calculate effective visible tabs (includes special tabs like Connections and Dashboard)
  // This ensures special tabs are preserved when TabRenderer updates the context
  const effectiveVisibleTabs = React.useMemo(() => {
    // Find special tabs from context that should be preserved
    const specialTabs = contextTabs.filter(
      tab => tab.name === CONNECTIONS_TAB_ID || tab.name === QUICK_CONNECTIONS_TAB_ID || tab.name === DASHBOARD_TAB_ID || tab.name === ITEM_PRICE_TAB_ID || tab.name === LOYALTY_TAB_ID || tab.name === ADDRESSES_TAB_ID
    );
    
    // Merge visibleTabs with special tabs
    const merged = [...visibleTabs];
    
    // Add special tabs that aren't already in visibleTabs
    for (const specialTab of specialTabs) {
      if (!merged.some(t => t.name === specialTab.name)) {
        merged.push(specialTab);
      }
    }
    
    return merged;
  }, [visibleTabs, contextTabs]);

  // Use context tabs if available, otherwise fall back to effective visible tabs
  // This ensures we're always working with the same tab structure
  // Make it reactive to context changes
  const effectiveTabs = React.useMemo(() => {
    return contextTabs.length > 0 ? contextTabs : effectiveVisibleTabs;
  }, [contextTabs, effectiveVisibleTabs]);

  // Calculate valid activeTab - prefer the current activeTab if it's valid
  // CRITICAL: This hook MUST be defined before any early returns
  const validActiveTab = React.useMemo(() => {
    if (effectiveTabs.length === 0) return "";
    
    // If activeTab exists and is valid, use it
    if (activeTab && effectiveTabs.some((tab) => tab.name === activeTab)) {
      return activeTab;
    }
    
    // Otherwise, use first tab as fallback
    return effectiveTabs[0]?.name || "";
  }, [activeTab, effectiveTabs]);

  // Update context when visible tabs change
  // NOTE: This effect has early returns, but they're inside the effect callback
  // (after the hook is called), so this is safe. The hook itself is unconditional.
  React.useEffect(() => {
    // Preserve special tabs (like Connections and Dashboard) that are managed by other hooks
    // These tabs have empty sections and are handled separately
    setTabs((prevTabs) => {
      // Find special tabs from prevTabs (they were added by useConnectionsTab, useQuickConnectionsTab, useDashboardTab, useItemPriceTab, etc.)
      // Using prevTabs prevents dependency loop - special tabs are preserved from previous state
      const specialTabs = prevTabs.filter(
        tab =>
          tab.name === CONNECTIONS_TAB_ID ||
          tab.name === QUICK_CONNECTIONS_TAB_ID ||
          tab.name === DASHBOARD_TAB_ID ||
          tab.name === ITEM_PRICE_TAB_ID ||
          tab.name === LOYALTY_TAB_ID ||
          tab.name === ADDRESSES_TAB_ID
      );
      
      // Merge visibleTabs with special tabs
      const mergedTabs = [...visibleTabs];
      
      // Add special tabs that aren't already in visibleTabs
      for (const specialTab of specialTabs) {
        if (!mergedTabs.some(t => t.name === specialTab.name)) {
          mergedTabs.push(specialTab);
        }
      }
      
      // Only update if tab STRUCTURE actually changed (prevents infinite loop).
      // Comparing only tab names is unsafe because many doctypes share tab IDs (e.g. "details").
      const signature = (tabsToSign: TabStructure[]) => {
        return tabsToSign.map((t) => {
          const sectionSig = (t.sections || []).map((s) => {
            const fieldnames = (s.columns || [])
              .flatMap((c) => (c.fields || []).map((f) => f.fieldname))
              .slice(0, 25)
              .join(",");
            return `${s.name}:${(s.columns || []).length}:${fieldnames}`;
          }).join("|");
          return `${t.name}:${t.label}:${(t.sections || []).length}:${sectionSig}`;
        }).join("||");
      };

      if (signature(prevTabs) === signature(mergedTabs)) {
        return prevTabs;
      }
      
      return mergedTabs;
    });

    // Calculate tabs for activeTab logic
    // Use visibleTabs only (special tabs are handled separately in SystemView)
    // Don't read contextTabs here to avoid dependency loop
    if (visibleTabs.length === 0) {
      // Check if activeTab is a special tab - if so, don't clear it
      if (activeTab !== CONNECTIONS_TAB_ID && activeTab !== QUICK_CONNECTIONS_TAB_ID && activeTab !== DASHBOARD_TAB_ID) {
        setActiveTab("");
      }
      return;
    }

    // For activeTab logic, we only care about visibleTabs
    // Special tabs are handled separately and won't interfere with activeTab selection
    // If current activeTab is not in visible tabs, switch to first visible tab
    // (unless it's a special tab, which is handled separately)
    if (
      activeTab && 
      activeTab !== CONNECTIONS_TAB_ID && 
      activeTab !== QUICK_CONNECTIONS_TAB_ID && 
      activeTab !== DASHBOARD_TAB_ID && 
      !visibleTabs.some(tab => tab.name === activeTab)
    ) {
      const firstTabName = visibleTabs[0]?.name;
      if (firstTabName) {
        setActiveTab(firstTabName);
      }
      return;
    }

    // Only set default if activeTab is empty
    // Never reset if activeTab is already set (even if it seems invalid)
    // This allows modal selections to work without interference
    if (!activeTab) {
      const firstTabName = visibleTabs[0]?.name;
      if (firstTabName) {
        setActiveTab(firstTabName);
      }
    }
    // We intentionally omit activeTab from dependencies to avoid re-running
    // when only the active tab changes (not the tabs array itself)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs, setTabs, setActiveTab]);

  // ============================================================================
  // NOW EARLY RETURNS ARE SAFE - ALL HOOKS HAVE BEEN CALLED
  // ============================================================================

  if (effectiveTabs.length === 0) {
    return <div className="text-muted-foreground">No fields available</div>;
  }

  // If only one tab, render without tab UI
  if (effectiveTabs.length === 1) {
    return (
      <div className={SECTION_GAP[effectiveDensity]}>
        {effectiveTabs[0].sections.map((section, sectionIndex) => (
          <div key={section.name || sectionIndex} className="empty:hidden" data-section-nav={sectionNavId(section)}>
            <SectionRenderer
              section={section}
              formData={formData}
              onFieldChange={onFieldChange}
              disabled={disabled}
              onSave={onSave || (() => {})}
              onSubmit={onSubmit}
              isSaving={isSaving}
              resetKey={resetKey}
              layoutStyle={layoutStyle}
              viewMode={viewMode}
              metaFields={meta?.fields}
              experienceViewStyle={experienceViewStyle}
              documentName={documentName}
              meta={meta}
              isNew={isNew}
              density={effectiveDensity}
              textSize={detailTextSize}
              fieldErrors={fieldErrors}
              onClearFieldError={onClearFieldError}
            />
          </div>
        ))}
      </div>
    );
  }

  // Don't render if we don't have a valid active tab
  if (!validActiveTab || effectiveTabs.length === 0) {
    return <div className="text-muted-foreground">No tabs available</div>;
  }

  // Find the active tab object
  const activeTabObj = effectiveTabs.find((tab) => tab.name === validActiveTab);

  // Manually render the active tab content instead of using Radix UI Tabs
  // This gives us more control and ensures content always renders when activeTab changes
  if (!activeTabObj) {
    return <div className="text-muted-foreground">Tab not found</div>;
  }

  return (
    <div
      key={validActiveTab}
      className={cn("w-full", SECTION_GAP[effectiveDensity])}
    >
      {activeTabObj.sections.map((section, sectionIndex) => (
        <div key={section.name || sectionIndex} className="empty:hidden" data-section-nav={sectionNavId(section)}>
          <SectionRenderer
            section={section}
            formData={formData}
            onFieldChange={onFieldChange}
            disabled={disabled}
            onSave={onSave || (() => {})}
            isSaving={isSaving}
            resetKey={resetKey}
            layoutStyle={layoutStyle}
            viewMode={viewMode}
            metaFields={meta?.fields}
            experienceViewStyle={experienceViewStyle}
            documentName={documentName}
            meta={meta}
            isNew={isNew}
            density={effectiveDensity}
            textSize={detailTextSize}
            fieldErrors={fieldErrors}
            onClearFieldError={onClearFieldError}
          />
        </div>
      ))}
    </div>
  );
}

